import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const BookingCalendar = ({ bookings = [], onDateSelect, selectedDate = new Date() }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  const [calendarDays, setCalendarDays] = useState([]);

  useEffect(() => {
    generateCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, bookings]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const firstDayWeekday = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days = [];

    for (let i = 0; i < firstDayWeekday; i++) {
      const prevMonthDay = new Date(year, month, -firstDayWeekday + i + 1);
      days.push({
        date: prevMonthDay,
        isCurrentMonth: false,
        bookings: []
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayBookings = getBookingsForDate(date);

      days.push({
        date: date,
        isCurrentMonth: true,
        bookings: dayBookings,
        isToday: isToday(date),
        isSelected: isSameDay(date, selectedDate)
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDay = new Date(year, month + 1, i);
      days.push({
        date: nextMonthDay,
        isCurrentMonth: false,
        bookings: []
      });
    }

    setCalendarDays(days);
  };

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return isSameDay(bookingDate, date);
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    if (onDateSelect) {
      onDateSelect(new Date());
    }
  };

  const handleDateClick = (day) => {
    if (onDateSelect) {
      onDateSelect(day.date);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'confirmed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  return (
    <div className="bg-card rounded-xl border border-divider p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-text-primary">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors"
          >
            اليوم
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-primary-light rounded-lg transition-colors"
            aria-label="الشهر السابق"
          >
            <ChevronRight className="w-5 h-5 text-text-primary" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-primary-light rounded-lg transition-colors"
            aria-label="الشهر التالي"
          >
            <ChevronLeft className="w-5 h-5 text-text-primary" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2">
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-text-secondary py-2"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => (
          <div
            key={index}
            onClick={() => day.isCurrentMonth && handleDateClick(day)}
            className={`
              min-h-24 p-2 rounded-lg border transition-all cursor-pointer
              ${day.isCurrentMonth
                ? 'bg-card border-divider hover:border-accent-gold hover:shadow-md'
                : 'bg-primary-light border-transparent opacity-50'
              }
              ${day.isToday ? 'ring-2 ring-accent-gold' : ''}
              ${day.isSelected ? 'bg-accent-gold/10 border-accent-gold' : ''}
            `}
          >
            <div className="flex flex-col h-full">
              <div className={`
                text-sm font-medium mb-2
                ${day.isToday ? 'text-accent-gold font-bold' : 'text-text-primary'}
                ${!day.isCurrentMonth ? 'text-text-muted' : ''}
              `}>
                {day.date.getDate()}
              </div>

              <div className="flex-1 space-y-1">
                {day.bookings.slice(0, 3).map((booking, idx) => (
                  <div
                    key={booking.id || idx}
                    className={`
                      text-xs p-1 rounded truncate
                      ${getStatusColor(booking.status)} text-white
                    `}
                    title={`${booking.hall?.name || 'قاعة'} - ${booking.purpose}`}
                  >
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span className="truncate">
                        {new Date(booking.startTime).toLocaleTimeString('ar-SA', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}

                {day.bookings.length > 3 && (
                  <div className="text-xs text-text-muted text-center mt-1">
                    +{day.bookings.length - 3} أخرى
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-6 mt-6 pt-6 border-t border-divider">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-yellow-500"></div>
          <span className="text-sm text-text-secondary">في الانتظار</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-sm text-text-secondary">مؤكد</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500"></div>
          <span className="text-sm text-text-secondary">ملغي</span>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
